import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { CurrencyViewStore } from "../../store";
import { CurrencyView } from "../view/CurrencyView";
import { CurrencyUpdateStore } from "../../store";
import { CurrencyUpdate } from "../update/CurrencyUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type CurrencyModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const CurrencyModalContent: React.FC<CurrencyModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new CurrencyUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new CurrencyViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <CurrencyView
            currencyViewStore={storeInfo.store as CurrencyViewStore}
          />
        )}
        {storeInfo.type === "update" && (
          <CurrencyUpdate
            currencyUpdateStore={storeInfo.store as CurrencyUpdateStore}
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
